import React from "react";
import { Box } from "@mui/material";
import SectionWithButtonsLayout from "../../shared/layout/SectionWithButtonsLayout";
import EstimateDatasourceCard from "./estimate/EstimateDatasourceCard";
import GcgrDatasourceCard from "./gcgr/GcgrDatasourceCard";
import ContractingDatasourceCard from "./contracting/ContractingDatasourceCard";
import CostModelDatasourceCard from "./costModel/CostModelDatasourceCard";
import { EdiphiDatasourceType, useDatasources } from "../../context/DatasourceContext";
import { EDIPHI_DATASOURCE_ORDER } from "./meta";
import { useLoading } from "../../context/LoadingContext";
import { useDatasourceLoader, useLoadTargets } from "../../hooks/useDatasourceLoader";

interface EdiphiDatasourcesSectionProps {
  handleAddClick: () => void;
  handleEditClick: (type: EdiphiDatasourceType) => void;
}

const CARDS: Partial<Record<EdiphiDatasourceType, React.ComponentType<{ onCardClick: () => void }>>> = {
  estimate: EstimateDatasourceCard,
  gcgr: GcgrDatasourceCard,
  contracting: ContractingDatasourceCard,
  "cost-model": CostModelDatasourceCard,
};

const EdiphiDatasourcesSection = ({ handleAddClick, handleEditClick }: EdiphiDatasourcesSectionProps) => {
  const { ediphiDatasources, hasDatasource } = useDatasources();
  const { globalLoading } = useLoading();
  const { loadDatasources } = useDatasourceLoader();
  const loadTargets = useLoadTargets();

  // Mirrors what the drawer offers, so the button disables exactly when every option there
  // is already taken.
  const canAddDatasource = EDIPHI_DATASOURCE_ORDER.some((type) => !hasDatasource("ediphi", type));

  const handleRefresh = () => {
    void loadDatasources(loadTargets(), { namePerDatasource: false });
  };

  const renderCards = () => {
    const cards = ediphiDatasources
      .map((type) => {
        const Card = CARDS[type];
        if (!Card) return null;
        // Dimmed in place rather than swapped for skeletons: these cards are still the right ones,
        // and blanking content already on screen is what the rediscovery paths deliberately avoid.
        return (
          <Box key={type} sx={{ opacity: globalLoading ? 0.5 : 1, pointerEvents: globalLoading ? "none" : "auto" }}>
            <Card onCardClick={() => handleEditClick(type)} />
          </Box>
        );
      })
      .filter(Boolean);

    return cards.length > 0 ? cards : null;
  };

  return (
    <>
      <SectionWithButtonsLayout
        header={{ text: "Ediphi Data Sources", img: "assets/ediphi_logo.svg" }}
        primaryButton={{ onClick: handleAddClick, disabled: !canAddDatasource || globalLoading }}
        secondaryButton={renderCards() ? { onClick: handleRefresh, disabled: globalLoading } : undefined}
        emptyStateText="Add an Ediphi Data Source to generate insights and reports using data from your Ediphi project."
      >
        {renderCards()}
      </SectionWithButtonsLayout>
    </>
  );
};

export default EdiphiDatasourcesSection;
